import styled from "@emotion/styled";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";

import { numberFormatLong } from "components/app_layout/Typography/Numbers";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "components/ui/chart/Chart";
import { ContactsCreditUsageMonth } from "lib/hooks/api/contacts/useContactsCreditUsageHistory";
import { pluralise } from "lib/utils";

type Props = {
  /** One entry per month on the axis, oldest first — gaps already zero-filled. */
  months: ContactsCreditUsageMonth[];
};

/**
 * Credits spent per calendar month. A bar per month rather than a line: the months are discrete
 * buckets that get compared against each other, and a line would imply a continuous series
 * between them.
 */
function ContactCreditsUsageChart({ months }: Props) {
  return (
    <StyledChartContainer
      aspectRatio="21 / 9"
      config={{
        creditsUsed: {
          label: "Credits used",
          color: "sysChart1",
          tooltipFormatter: numberFormatLong,
        },
      }}
    >
      <BarChart accessibilityLayer data={months} margin={{ top: 8, right: 8 }}>
        <CartesianGrid vertical={false} />
        <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} minTickGap={8} />
        <YAxis
          width={48}
          tickLine={false}
          axisLine={false}
          allowDecimals={false}
          // Floor the top of the scale at 1 so a company that has spent nothing still gets a
          // readable axis rather than a degenerate 0–0 one.
          domain={[0, (dataMax: number) => Math.max(dataMax, 1)]}
        />
        <ChartTooltip
          content={
            // The formatter supplies the "credits used" wording; an empty month
            // reads as "0 credits used".
            <ChartTooltipContent
              formatter={(value) => `${pluralise(Number(value), "credit", true)} used`}
            />
          }
        />
        <Bar dataKey="creditsUsed" fill="var(--color-creditsUsed)" radius={4} maxBarSize={48} />
      </BarChart>
    </StyledChartContainer>
  );
}

const StyledChartContainer = styled(ChartContainer)({
  width: "100%",
  maxHeight: 320,
});

export default ContactCreditsUsageChart;
